import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Spin, Button } from "antd";
import { useNavigate, Link } from "react-router-dom";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import Navbar from "./Navbar";
import AuthServ from "../service/AuthServ";
import Notify from "../util/Notify";
import OTPInput from "../component/OTPInput";
import "../css/loginAndSignUp.css";
import AuthVisualHero from "../component/AuthVisualHero";

const ForgotPassword = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();

    // State
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [identifier, setIdentifier] = useState("");
    const [identifierType, setIdentifierType] = useState("email");
    const [otpValue, setOtpValue] = useState("");

    // Timer
    const [resending, setResending] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const timerRef = useRef(null);

    // Timer effect
    useEffect(() => {
        if (resendTimer > 0) {
            timerRef.current = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [resendTimer]);

    // Helpers
    const detectIdentifierType = (value) => {
        if (!value) return "email";
        const trimmed = value.trim();
        if (trimmed.includes('@')) return "email";
        if (/^\d+$/.test(trimmed)) return "mobile";
        return "email";
    };

    const validateIdentifier = (value) => {
        const type = detectIdentifierType(value);
        if (type === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) return { valid: false, message: "Invalid email format" };
        } else {
            if (!/^\d{10}$/.test(value)) return { valid: false, message: "Invalid mobile number (10 digits)" };
        }
        return { valid: true };
    };

    // Step 1: Send OTP
    const handleSendOtp = async (values) => {
        const val = values.identifier.trim();
        const type = detectIdentifierType(val);
        const validation = validateIdentifier(val);

        if (!validation.valid) {
            Notify.error(null, validation.message);
            return;
        }

        setLoading(true);
        try {
            const response = await AuthServ.forgotPassword({ email: val });

            setIdentifier(val);
            setIdentifierType(type);
            setStep(2);
            setResendTimer(30);
            Notify.success("OTP Sent", response?.message || "Verification code sent.");
        } catch (error) {
            Notify.error(error, "Failed to send OTP.");
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendTimer > 0) return;
        setResending(true);
        try {
            await AuthServ.forgotPassword({ email: identifier });
            setResendTimer(30);
            Notify.success("OTP Resent", "Verification code sent again.");
        } catch (error) {
            Notify.error(error, "Failed to resend OTP.");
        } finally {
            setResending(false);
        }
    };

    // Step 2: Verify OTP
    const handleVerifyOtp = async () => {
        if (otpValue.length !== 6) {
            Notify.error(null, "Please enter all 6 digits of the OTP.");
            return;
        }

        setLoading(true);
        try {
            const response = await AuthServ.verifyOtp({ email: identifier, otp: otpValue });
            Notify.success("OTP Verified", response?.message || "OTP verified successfully!");
            setStep(3);
        } catch (error) {
            console.error("Verify OTP Error:", error);
            Notify.error(error, "Invalid OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Reset Password
    const handleResetPassword = async (values) => {
        if (values.newPassword !== values.confirmPassword) {
            Notify.error(null, "Passwords do not match!");
            return;
        }

        setLoading(true);
        try {
            const response = await AuthServ.resetPassword({ email: identifier, newPassword: values.newPassword });
            Notify.success("Password Reset", response?.message || "Password has been reset successfully!");
            navigate("/login");
        } catch (error) {
            console.error("Reset Password Error:", error);
            Notify.error(error, "Unable to reset password. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const getStepTitle = () => {
        switch (step) {
            case 1: return "Forgot Password";
            case 2: return "Verify OTP";
            case 3: return "Reset Password";
            default: return "Forgot Password";
        }
    };

    const getStepSubtitle = () => {
        switch (step) {
            case 1: return "Enter your email or mobile number to receive a verification code";
            case 2: return `Enter the code sent to ${identifier}`;
            case 3: return "Create a new password for your account";
            default: return "";
        }
    };

    return (
        <div className="nz-auth-page-layout">
            <Navbar />
            <div className="nz-auth-card-wrapper">
                <div className="nz-auth-split-container">
                {/* 🎨 Left Side - Visual Showcase Panel */}
                <AuthVisualHero activeStep={3} />

                {/* 🚪 Right Side - Elegant, ultra-clean form panel */}
                <div className="nz-auth-form-side">
                    <div className="nz-split-form-wrapper">

                        <h2>{getStepTitle()}</h2>
                        <p className="nz-split-subtitle">{getStepSubtitle()}</p>

                        <Spin spinning={loading}>
                            {/* Step 1: Single input - auto-detect email or mobile */}
                            {step === 1 && (
                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={handleSendOtp}
                                    requiredMark={false}
                                    size="large"
                                >
                                    <Form.Item
                                        name="identifier"
                                        className="nz-premium-form-item"
                                        rules={[
                                            { required: true, message: "Please enter your email or mobile number!" }
                                        ]}
                                    >
                                        <Input
                                            prefix={<UserOutlined style={{ color: '#cbd5e1', fontSize: '16px', marginRight: '6px' }} />}
                                            placeholder="Email or Mobile Number"
                                        />
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            block
                                            className="nz-split-submit-btn"
                                        >
                                            Send OTP
                                        </Button>
                                    </Form.Item>
                                </Form>
                            )}

                            {/* Step 2: OTP Verification - 6 separate boxes */}
                            {step === 2 && (
                                <div>
                                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                                        <OTPInput
                                            value={otpValue}
                                            onChange={setOtpValue}
                                            length={6}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            type="primary"
                                            onClick={handleVerifyOtp}
                                            className="nz-split-submit-btn"
                                            style={{ width: '200px', height: '40px', borderRadius: '8px' }}
                                        >
                                            Verify OTP
                                        </Button>
                                    </div>

                                    {/* Resend OTP */}
                                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>
                                            Didn't receive code?{' '}
                                        </span>
                                        <Button
                                            type="link"
                                            onClick={handleResendOtp}
                                            loading={resending}
                                            disabled={resendTimer > 0}
                                            style={{
                                                fontWeight: '600',
                                                fontSize: '14px',
                                                padding: 0,
                                                color: resendTimer > 0 ? '#bfbfbf' : 'var(--nearzon-orange)',
                                                height: 'auto'
                                            }}
                                        >
                                            {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                                        </Button>
                                    </div>

                                    {/* Change identifier */}
                                    <div style={{ textAlign: 'center', marginTop: '12px' }}>
                                        <Button
                                            type="link"
                                            onClick={() => {
                                                setStep(1);
                                                setOtpValue("");
                                            }}
                                            style={{ padding: 0, height: 'auto' }}
                                        >
                                            <span style={{ fontWeight: '700', color: '#ef4444', fontSize: '13px' }}>
                                                Change {identifierType === "email" ? "Email" : "Mobile"}
                                            </span>
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Reset Password */}
                            {step === 3 && (
                                <Form layout="vertical" onFinish={handleResetPassword} requiredMark={false} size="large">
                                    <Form.Item
                                        name="newPassword"
                                        className="nz-premium-form-item"
                                        rules={[
                                            { required: true, message: "Please enter a new password!" },
                                            { min: 6, message: "Password must be at least 6 characters" }
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined style={{ color: '#cbd5e1', fontSize: '16px', marginRight: '6px' }} />}
                                            placeholder="New Password"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="confirmPassword"
                                        className="nz-premium-form-item"
                                        rules={[
                                            { required: true, message: "Please confirm your password!" },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('newPassword') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Passwords do not match!'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined style={{ color: '#cbd5e1', fontSize: '16px', marginRight: '6px' }} />}
                                            placeholder="Confirm Password"
                                        />
                                    </Form.Item>

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        className="nz-split-submit-btn"
                                    >
                                        Reset Password
                                    </Button>
                                </Form>
                            )}
                        </Spin>

                        <div className="nz-split-footer">
                            Remember your password?{" "}
                            <Link to="/login" className="nz-split-footer-link">
                                Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
};

export default ForgotPassword;
